/**
 * ParticipantPopover — hover/click card that surfaces a single room participant's
 * profile (image, name, blurb, bio) without taking over the sidebar.
 *
 * Wraps an avatar trigger in a Radix HoverCard. The blurb line shows the same
 * demographic snippet displayed in the setup wizard (age/gender for B2C,
 * job title/industry for B2B). Bio is sourced first from the cheap participant
 * DTO (immediate render), then upgraded with the richer
 * `FeaturedTwin.profileCard.bio` / `personaSummary` once the lazy SWR fetch
 * resolves.
 */

import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AtSign, BookOpen, MapPin } from 'lucide-react'
import useSWR from 'swr'

import { SubsegmentChip } from '@/components/twin-chat'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Text } from '@/components/ui/Text'
import { usePersonaThumbFallback } from '@/components/shared/PersonaThumbImg'
import { TwinBio } from '@/components/shared/TwinBio'
import { useIsNarrowViewport } from '@/hooks/useIsNarrowViewport'
import { twinsApi } from '@/lib/api'
import { buildBlurbLine, buildLocationLine, inferAudienceType } from '@/lib/utils/twin-blurb'

import { ChatRoomParticipantDto } from '@digital-twins/shared-types'
import type { AudienceTag } from '@digital-twins/shared-types'

interface ParticipantPopoverProps {
  participant: ChatRoomParticipantDto
  populationId: string | null | undefined
  /** Session-level subsegment tags (e.g. a Quick Study focus group scoped to a saved
   *  subsegment). Shown as colored chips in the profile since every seated twin shares
   *  the segment. Absent/empty → nothing rendered (byte-identical to a normal chat). */
  segmentTags?: Array<AudienceTag>
  /** Decision L: prefill this twin's @mention in the composer. Absent hides Ask (guests, no composer). */
  onAsk?: () => void
  /** Hide the photo when the trigger already shows the portrait (the empty-room cover flow); the story stays. */
  hidePhoto?: boolean
  children: React.ReactNode
}

const FALLBACK_IMAGE = '/images/twin-fallback.png'

function ShimmerBar(props: { className?: string }) {
  return (
    <div
      className={`animate-pulse rounded bg-twin-chat-bubble/40 ${props.className ?? ''}`}
    />
  )
}

export function ParticipantPopover(props: ParticipantPopoverProps) {
  const { participant, populationId, segmentTags, onAsk, hidePhoto = false, children } = props

  const { t } = useTranslation('app')
  const [open, setOpen] = useState(false)
  // Set by Ask: the sheet must not hand focus back to its trigger, the composer takes it.
  const isAskingRef = useRef(false)
  // Below md the hover card would overflow a phone and never opens on touch:
  // the same profile opens as a bottom sheet on tap instead.
  const isNarrow = useIsNarrowViewport()
  const { displayUrl: thumbDisplayUrl, handleImageError } = usePersonaThumbFallback(participant.imageUrl, 128)

  const canFetchTwin = Boolean(open && populationId && participant.featuredTwinId)
  const { data: twin, isLoading: twinLoading, error: twinError } = useSWR(
    canFetchTwin
      ? `twin-${populationId}-${participant.personaId}`
      : null,
    () => twinsApi.getFeaturedTwin(populationId!, participant.personaId),
    // Cap SWR's default unlimited retry loop — a persona with no featured-twin
    // record 404s every time. One retry still covers a transient network blip.
    { revalidateOnFocus: false, errorRetryCount: 1 },
  )
  const participantHighlights = participant.highlights?.length ? participant.highlights : undefined
  // Bullets are the intended presentation and always win. The prose paragraph
  // is only a fallback: a non-featured twin never fetches a profile card
  // (canFetchTwin requires featuredTwinId), so without it the card renders
  // empty even though the backend supplies a persona bio on the participant.
  const bulletCard = participantHighlights
    ? { bio: participant.bio, highlights: participantHighlights }
    : twin?.profileCard?.highlights?.length
      ? twin.profileCard
      : null
  const paragraphCard = participant.bio?.trim()
    ? { bio: participant.bio, highlights: undefined }
    : null
  const profileCard = bulletCard ?? paragraphCard ?? twin?.profileCard ?? null
  // A card counts as content when it has bullet highlights OR an intro paragraph.
  const hasBioContent = Boolean(profileCard?.highlights?.length || profileCard?.bio?.trim())

  const demographics = (twin?.demographics ?? {}) as Record<string, string>
  const audienceType = inferAudienceType(demographics)
  const locationLine = buildLocationLine(demographics)
  // The authored one-liner from the profile card wins over the derived
  // demographic blurb; the location half of the line is unaffected.
  const authoredTagline = twin?.profileCard?.tagline?.trim() || null
  const blurbLine = authoredTagline ?? buildBlurbLine(demographics, audienceType)
  const detailLine = [locationLine, blurbLine].filter(Boolean).join(' · ')

  const showDetailSkeleton = twinLoading && !detailLine && !participant.tagline
  const showBioSkeleton = canFetchTwin && twinLoading && !twinError

  const onAskClick = () => {
    isAskingRef.current = true
    setOpen(false)
    onAsk?.()
  }

  const profileBody = (
        <div className={`flex flex-col p-5 ${isNarrow ? 'pr-12' : ''}`}>
          {/* Header: photo + name + blurb */}
          <div className="flex items-start gap-4">
            {!hidePhoto && (
              <div className="h-[88px] w-[88px] shrink-0 overflow-hidden rounded-xl bg-twin-chat-bubble/40">
                {thumbDisplayUrl ? (
                  <img
                    src={thumbDisplayUrl}
                    alt={participant.displayName}
                    className="h-full w-full object-cover object-top"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      handleImageError(() => {
                        target.src = FALLBACK_IMAGE
                      })
                    }}
                  />
                ) : (
                  <div className="relative flex h-full w-full items-center justify-center text-3xl font-bold text-twin-chat-fg/40">
                    {participant.displayName[0]}
                    {twinLoading && (
                      <div className="absolute inset-0 animate-pulse bg-twin-chat-bubble/30" />
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="flex min-w-0 flex-col gap-1.5 pt-0.5">
              <h3 className="font-display text-[22px] font-normal leading-[1.2] tracking-[-0.01em] text-twin-chat-fg">
                {participant.displayName}
              </h3>
              {detailLine ? (
                <div className="flex items-center gap-1.5 text-twin-chat-muted">
                  {locationLine && <MapPin className="h-3 w-3 shrink-0" />}
                  <Text size="sm" className="line-clamp-2 leading-[1.4] text-twin-chat-muted">
                    {detailLine}
                  </Text>
                </div>
              ) : !detailLine && participant.tagline ? (
                <Text
                  size="sm"
                  className="leading-[1.4] text-twin-chat-muted"
                >
                  {participant.tagline}
                </Text>
              ) : showDetailSkeleton ? (
                <div className="flex flex-col gap-1.5 pt-0.5">
                  <ShimmerBar className="h-3 w-28" />
                  <ShimmerBar className="h-3 w-20" />
                </div>
              ) : null}
              {onAsk && (
                <button
                  type="button"
                  onClick={onAskClick}
                  aria-label={t('roomPane.askAria', { name: participant.displayName })}
                  data-testid="participant-popover-ask"
                  className="mt-1 inline-flex w-fit items-center gap-1.5 rounded-full bg-twin-chat-fg/10 px-3 py-1 text-sm font-medium text-twin-chat-fg transition-colors hover:bg-twin-chat-fg/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-twin-chat-accent/50"
                >
                  <AtSign className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('roomPane.ask')}
                </button>
              )}
            </div>
          </div>

          {segmentTags && segmentTags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5" data-testid="participant-segment-tags">
              {segmentTags.map((tag) => {
                const color = (tag.color ?? '').trim()
                return (
                  <span
                    key={tag.id}
                    title={tag.description ? `${tag.name} — ${tag.description}` : tag.name}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-tight ${
                      color ? '' : 'border-transparent bg-twin-chat-bubble/40 text-twin-chat-muted'
                    }`}
                    style={color ? { borderColor: `${color}59`, color, backgroundColor: `${color}14` } : undefined}
                  >
                    {color && (
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: color }}
                        aria-hidden="true"
                      />
                    )}
                    <span className="max-w-[160px] truncate">{tag.name}</span>
                  </span>
                )
              })}
            </div>
          )}

          {participant.audienceTagNames && participant.audienceTagNames.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {participant.audienceTagNames.map((tagName) => (
                <span
                  key={tagName}
                  className="inline-flex items-center rounded-full bg-twin-chat-bubble/40 px-2 py-0.5 text-[11px] leading-tight text-twin-chat-muted"
                >
                  {tagName}
                </span>
              ))}
            </div>
          )}

          {participant.selectionTags && participant.selectionTags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {participant.selectionTags.map(
                (tag) => (
                  <div
                    key={tag.chip}
                    className="rounded-md bg-muted px-2 py-0.5"
                    title={`${tag.displayQuestion}: ${tag.displayAnswer}`}
                  >
                    <span className="text-xs font-medium">{tag.chip}</span>
                    <span className="ml-1 text-[10px] text-muted-foreground">
                      {tag.displayQuestion}: {tag.displayAnswer}
                    </span>
                  </div>
                ),
              )}
            </div>
          )}

          {hasBioContent ? (
            <div className="mt-4 border-t border-twin-chat-divider/20 pt-4">
              <TwinBio
                profileCard={profileCard}
                introClassName="text-twin-chat-fg"
                highlightClassName="text-twin-chat-muted"
              />

            </div>
          ) : showBioSkeleton ? (
            <div className="border-t border-twin-chat-divider/20 pt-4">
              <div className="flex flex-col gap-2">
                <ShimmerBar className="h-3 w-full" />
                <ShimmerBar className="h-3 w-5/6" />
                <ShimmerBar className="h-3 w-3/4" />
              </div>
            </div>
          ) : null}

          {/* Knowledge Base MVP — per-twin briefing derived from the study's knowledge
              documents (`applyParticipantKnowledge`). Absent when the twin had no match
              or the study has no documents, so this renders nothing there. */}
          {participant.knowledgeBriefing ? (
            <div
              className="border-t border-twin-chat-divider/20 pt-4"
              data-testid="participant-knowledge-briefing"
            >
              <p className="flex items-center gap-1.5 text-xs font-medium tracking-wide text-twin-chat-fg">
                <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
                From your data
              </p>
              <div className="mt-1 max-h-40 overflow-y-auto">
                <Text size="sm" className="leading-[1.55] text-twin-chat-muted">
                  {participant.knowledgeBriefing}
                </Text>
              </div>
            </div>
          ) : null}

          {/* Quick Study only — authored subsegment chips, placed AFTER the blurb.
              Absent/[] for every non-Quick-Study caller, so this renders nothing there. */}
          {participant.subsegmentLabels && participant.subsegmentLabels.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="qs-twin-popover-segment-chips">
              {participant.subsegmentLabels.map((label) => (
                <SubsegmentChip key={label} label={label} testId="qs-twin-popover-segment-chip" />
              ))}
            </div>
          )}
        </div>
  )

  if (isNarrow) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>{children}</SheetTrigger>
        <SheetContent
          side="bottom"
          aria-describedby={undefined}
          data-testid="participant-profile-sheet"
          onCloseAutoFocus={(event) => {
            if (isAskingRef.current) {
              isAskingRef.current = false
              event.preventDefault()
            }
          }}
          className="max-h-[85dvh] overflow-y-auto rounded-t-2xl border-twin-chat-divider/60 bg-twin-chat-surface p-0 text-twin-chat-fg"
        >
          <SheetTitle className="sr-only">{participant.displayName}</SheetTitle>
          {profileBody}
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <HoverCard open={open} openDelay={150} closeDelay={120} onOpenChange={setOpen}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        align="start"
        side="right"
        sideOffset={8}
        className="w-[380px] border-twin-chat-divider/60 bg-twin-chat-surface p-0 text-twin-chat-fg"
        data-testid="participant-profile-hover-card"
      >
        {profileBody}
      </HoverCardContent>
    </HoverCard>
  )
}
